import React, { useState, useEffect, useRef } from 'react'
import { Box, Paper } from '@mui/material'
import { TrackedLink } from '../TrackedLink'
import { CaretIcon } from '../icons/CaretIcon'

export type NavItemProps = {
  label: string
  to?: string
  dropdownContent?: React.ReactNode
  color?: any
}

const NavItem = ({ label, dropdownContent, color, to }: NavItemProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsOpen((prev) => !prev)
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleFocusOut = (e: FocusEvent) => {
      // Close only if focus moved completely outside the container
      if (!container.contains(e.relatedTarget as Node)) {
        setIsOpen(false)
      }
    }

    container.addEventListener('focusout', handleFocusOut)
    // eslint-disable-next-line consistent-return
    return () => container.removeEventListener('focusout', handleFocusOut)
  }, [])

  if (to) {
    return (
      <TrackedLink
        key={label}
        variant="cta"
        sx={{
          pt: '24px',
          pb: '24px',
          color: 'inherit',
          '&:hover': {
            backgroundColor: 'unset',
            color: color || 'primary.main',
          },
        }}
        to={to}
      >
        <Box
          display="flex"
          alignItems="center"
          gap="8px"
          sx={{ pointerEvents: 'none' }}
        >
          {label}
        </Box>
      </TrackedLink>
    )
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        '&:hover .dropdown-content': { display: 'block' },
      }}
    >
      <TrackedLink
        key={label}
        variant="cta"
        component="button"
        onKeyDown={handleKeyDown}
        sx={{
          pt: '24px',
          pb: '24px',
          color: color ? `${color}!important` : 'primary.main',
          '&:hover': { backgroundColor: 'unset' },
        }}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Box
          display="flex"
          alignItems="center"
          gap="8px"
          sx={{ pointerEvents: 'none' }}
        >
          {label}
          {dropdownContent ? <CaretIcon color={color || 'primary'} /> : null}
        </Box>
      </TrackedLink>

      {dropdownContent && (
        <Paper
          className="dropdown-content"
          sx={{
            display: isOpen ? 'block' : 'none',
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            borderRadius: '16px!important',
            boxShadow: '0px 8px 24px 0px rgba(69, 73, 77, 0.08)',
            zIndex: 1,
          }}
        >
          {dropdownContent}
        </Paper>
      )}
    </Box>
  )
}

export default NavItem

NavItem.defaultProps = {
  to: '',
}
